import { useTranslate } from "@opensea/next-translate"
import React from "react"
import { graphql, useFragment } from "react-relay"
import { PaymentAssetMedia } from "@/components/common/media/PaymentAssetMedia"
import type { Unwrap_metadata$key } from "@/lib/graphql/__generated__/Unwrap_metadata.graphql"
import { BigNumber, bn } from "@/lib/helpers/numberUtils"
import { TxItem } from "../components/TxItem.react"
import { useTransactionUrl } from "../hooks/useTransactionUrl"
import type { TxState } from "../types"

type UnwrapProps = {
  metadata: Unwrap_metadata$key
  state: TxState
  transactionHash: string | undefined
  nonce?: number
  createdDate?: string
}

export function Unwrap({
  metadata: metadataKey,
  state,
  transactionHash,
  nonce,
  createdDate,
}: UnwrapProps) {
  const t = useTranslate("transactions")
  const transactionUrl = useTransactionUrl(transactionHash)

  const { sentAssetQuantities, receivedAssetQuantities } =
    useFragment<Unwrap_metadata$key>(
      graphql`
        fragment Unwrap_metadata on TransactionMetadataType {
          sentAssetQuantities {
            asset {
              symbol
              decimals
              usdSpotPrice
            }
            quantity
          }
          receivedAssetQuantities {
            asset {
              ...PaymentAssetMedia_asset
            }
          }
        }
      `,
      metadataKey,
    )

  // Only one sent asset is involved in a wrap transaction
  const sentAssetSymbol = sentAssetQuantities[0].asset.symbol ?? "WETH"
  const sentAssetUsdSpotPrice = sentAssetQuantities[0].asset.usdSpotPrice ?? 1
  const sentAssetDecimals = sentAssetQuantities[0].asset.decimals
  const sentAssetQuantity = bn(
    BigNumber.sum(...sentAssetQuantities.map(aq => aq.quantity)),
    sentAssetDecimals,
  )

  const isComplete = transactionHash && state !== "PENDING"
  const isPending = state === "PENDING"

  return (
    <TxItem href={isPending ? undefined : transactionUrl} target="_blank">
      <TxItem.Avatar state={state}>
        <PaymentAssetMedia asset={receivedAssetQuantities.at(0)?.asset} />
      </TxItem.Avatar>
      <TxItem.Content>
        <TxItem.Title>
          {t("txItem.unwrap.title", "Withdraw from offer balance")}
        </TxItem.Title>
        <TxItem.Description>
          {t("txItem.unwrap.description", "Unwrap {{symbol}}", {
            symbol: sentAssetSymbol,
          })}
        </TxItem.Description>
      </TxItem.Content>
      {isComplete ? (
        <TxItem.Value
          symbol={sentAssetSymbol}
          unit={sentAssetQuantity}
          usd={bn(sentAssetQuantity).times(sentAssetUsdSpotPrice)}
        />
      ) : isPending ? (
        <TxItem.Pending
          createdDate={createdDate}
          href={transactionUrl}
          nonce={nonce}
        />
      ) : (
        <TxItem.Requested />
      )}
    </TxItem>
  )
}
